import React, { createContext, useContext, useReducer, useEffect } from 'react';
import axios from 'axios';

const QuotationContext = createContext();

// Quotation reducer
const quotationReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_QUOTATION':
      const existingItem = state.items.find(item => item.id === action.payload.id);
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id
              ? {
                  ...item,
                  quantity: item.quantity + action.payload.quantity,
                  total_price: (item.quantity + action.payload.quantity) * item.unit_price
                }
              : item
          )
        };
      } else {
        return {
          ...state,
          items: [...state.items, action.payload]
        };
      }
    
    case 'REMOVE_FROM_QUOTATION':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };
    
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? {
                ...item,
                quantity: action.payload.quantity,
                total_price: item.unit_price * action.payload.quantity
              }
            : item
        )
      };

    case 'UPDATE_UNIT_PRICE':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? {
                ...item,
                unit_price: action.payload.price,
                total_price: action.payload.price * item.quantity
              }
            : item
        )
      };
    
    case 'CLEAR_QUOTATION':
      return {
        ...state,
        items: []
      };
    
    case 'LOAD_QUOTATION':
      return {
        ...state,
        items: action.payload
      };
    
    case 'SET_CUSTOMER_DETAILS':
      return {
        ...state,
        customerDetails: action.payload
      };
    
    default:
      return state;
  }
};

// Quotation provider component
export const QuotationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(quotationReducer, {
    items: [],
    customerDetails: {
      name: '',
      email: '',
      phone: ''
    }
  });

  // Load quotation from localStorage on mount
  useEffect(() => {
    const savedQuotation = localStorage.getItem('quotation');
    if (savedQuotation) {
      try {
        const parsedQuotation = JSON.parse(savedQuotation);
        if (parsedQuotation.items) {
          dispatch({ type: 'LOAD_QUOTATION', payload: parsedQuotation.items });
        }
        if (parsedQuotation.customerDetails) {
          dispatch({ type: 'SET_CUSTOMER_DETAILS', payload: parsedQuotation.customerDetails });
        }
      } catch (error) {
        console.error('Error loading quotation from localStorage:', error);
      }
    }
  }, []);

  // Save quotation to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('quotation', JSON.stringify(state));
  }, [state]);

  // Robust image parser (handles both product data and cart item data)
  const getFirstImageUrl = (item) => {
    // If it's already a processed image (from cart), use it directly
    if (item.image) {
      return item.image;
    }
    
    // If it's raw product data with images field, process it
    if (item.images) {
      let imageUrl = "";
      if (Array.isArray(item.images)) {
        imageUrl = item.images[0];
      } else if (typeof item.images === "string" && item.images.trim() !== "") {
        try {
          const parsed = JSON.parse(item.images);
          if (Array.isArray(parsed) && parsed.length > 0) {
            imageUrl = parsed[0];
          } else {
            imageUrl = item.images.split(",")[0].trim();
          }
        } catch {
          imageUrl = item.images.split(",")[0].trim();
        }
      }
      return imageUrl;
    }
    
    return null;
  };

  // Quotation actions
  const addToQuotation = (product, quantity = 1) => {
    const imageUrl = getFirstImageUrl(product);
    console.log('QuotationContext addToQuotation - product:', product);
    console.log('QuotationContext addToQuotation - imageUrl:', imageUrl);
    
    const basePrice = Number(product.price) || 0;

    dispatch({
      type: 'ADD_TO_QUOTATION',
      payload: {
        id: product.id,
        product_id: product.id,
        name: product.name,
        price: basePrice,
        base_price: basePrice,
        unit_price: basePrice,
        image: imageUrl,
        quantity,
        total_price: basePrice * quantity
      }
    });
  };

  const removeFromQuotation = (productId) => {
    dispatch({
      type: 'REMOVE_FROM_QUOTATION',
      payload: productId
    });
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromQuotation(productId);
    } else {
      dispatch({
        type: 'UPDATE_QUANTITY',
        payload: { id: productId, quantity }
      });
    }
  };

  const updateUnitPrice = (productId, price) => {
    const numericPrice = typeof price === 'number' ? price : parseFloat(price);
    if (Number.isNaN(numericPrice) || numericPrice <= 0) {
      return;
    }
    dispatch({
      type: 'UPDATE_UNIT_PRICE',
      payload: { id: productId, price: parseFloat(numericPrice.toFixed(2)) }
    });
  };

  const clearQuotation = () => {
    dispatch({ type: 'CLEAR_QUOTATION' });
  };

  const setCustomerDetails = (details) => {
    dispatch({
      type: 'SET_CUSTOMER_DETAILS',
      payload: details
    });
  };

  // Create quotation
  const createQuotation = async (customerDetails, meta = {}) => {
    try {
      const quotationData = {
        items: state.items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
          product_name: item.name
        })),
        customer_details: customerDetails,
        meta
      };

      const response = await axios.post('/quotations', quotationData);
      return response.data;
    } catch (error) {
      console.error('Error creating quotation:', error);
      throw error;
    }
  };

  // Calculate quotation totals
  const quotationTotal = state.items.reduce((total, item) => total + (item.unit_price * item.quantity), 0);
  const quotationCount = state.items.reduce((count, item) => count + item.quantity, 0);

  const value = {
    items: state.items,
    customerDetails: state.customerDetails,
    quotationTotal,
    quotationCount,
    addToQuotation,
    removeFromQuotation,
    updateQuantity,
    updateUnitPrice,
    clearQuotation,
    setCustomerDetails,
    createQuotation
  };

  return (
    <QuotationContext.Provider value={value}>
      {children}
    </QuotationContext.Provider>
  );
};

// Custom hook to use quotation context
export const useQuotation = () => {
  const context = useContext(QuotationContext);
  if (!context) {
    throw new Error('useQuotation must be used within a QuotationProvider');
  }
  return context;
};
